import { Component, OnInit } from '@angular/core';
import { Entry } from 'contentful';
import { ActivatedRoute } from '@angular/router';
import { StoreService } from '../../services/store/store.service';
import { LocalizationService } from '../../services/localization/localization.service';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { TestResultService } from '../../services/test-result/test-result.service';

@Component({
  selector: 'app-international-test',
  templateUrl: './international-test.component.html',
  styleUrls: ['./international-test.component.scss'],
})
export class InternationalTestComponent implements OnInit {
  public content: Entry<any> | undefined;
  public questionIndex = 0;
  public categoryIndex = 0;
  public currentQuestion: any;
  public co2e = 0;
  public testResult: any;
  public answers: any;
  public country: any;
  public chartData = [];

  constructor(
    activatedRoute: ActivatedRoute,
    private store: StoreService,
    private localizationService: LocalizationService,
    private analyticsService: AnalyticsService,
    private testResultService: TestResultService,
  ) {
    this.content = activatedRoute.snapshot.data.content;
  }

  get currentCategory() {
    if (this.content) {
      return this.content.fields.calculatorData[this.categoryIndex];
    }
  }

  get isFirstQuestion() {
    return this.questionIndex === 0 && this.categoryIndex === 0;
  }

  ngOnInit(): void {
    if (this.store.country.value) {
      this.country = this.store.country.value;
    } else {
      this.localizationService.navigate(['start-test']);
    }

    if (this.content) {
      const categories = this.content.fields.calculatorData;
      this.currentQuestion =
        categories[this.categoryIndex].questions[this.questionIndex];
    }
    this.testResult = this.calculateResult([]);
  }

  public onAnswered(answers: Array<any>): void {
    this.answers = answers;
    this.testResult = this.calculateResult(this.answers);
    this.updateChart();
    this.nextQuestion();
  }

  public async nextQuestion(): Promise<void> {
    const categories = this.content.fields.calculatorData;

    if (
      this.categoryIndex === 1 &&
      this.questionIndex === 0 &&
      this.answers.how_far_do_you_travel_by_car_each_week.id ===
        'dont-travel-by-car'
    ) {
      this.categoryIndex = 1;
      this.questionIndex = 2;
    } else if (
      categories[this.categoryIndex].questions.length >
      this.questionIndex + 1
    ) {
      // Next question
      this.questionIndex++;
    } else if (categories.length > this.categoryIndex + 1) {
      // Next category
      this.questionIndex = 0;
      this.categoryIndex++;
    } else {
      // Last question...
      this.analyticsService.finishInternationalTest(
        this.country.country,
        this.testResult.co2e,
      );

      this.testResultService.setTestResult(this.testResult).then(() => {
        this.localizationService.navigate(['result']);
      });
    }

    this.currentQuestion =
      categories[this.categoryIndex].questions[this.questionIndex];
  }

  previousQuestion(): void {
    const categories = this.content.fields.calculatorData;

    if (
      this.categoryIndex === 1 &&
      this.questionIndex === 2 &&
      this.answers.how_far_do_you_travel_by_car_each_week.id ===
        'dont-travel-by-car'
    ) {
      this.categoryIndex = 1;
      this.questionIndex = 0;
    } else if (this.questionIndex > 0) {
      // Previous question
      this.questionIndex--;
    } else if (this.questionIndex === 0 && this.categoryIndex > 0) {
      // Previous category
      this.categoryIndex--;
      const lastQuestionIndex =
        categories[this.categoryIndex].questions.length - 1;
      this.questionIndex = lastQuestionIndex;
    }

    this.currentQuestion =
      categories[this.categoryIndex].questions[this.questionIndex];
  }

  getLocalizedValue(values): any {
    /* tslint:disable */
    if (this.country && values[this.country.region]) {
      return values[this.country.region];
    } else {
      return values['world'];
    }
    /* tslint:enable */
  }

  updateChart(): void {
    const chartData = [];
    this.testResult.categoryCO2e.map((categoryCO2e, index) => {
      chartData.push({
        name: categoryCO2e.category.title,
        value: categoryCO2e.co2e,
        color: categoryCO2e.category.color,
        icon: categoryCO2e.category.icon,
      });
    }, this);
    this.chartData = chartData;
  }

  calculateProgressBarWidth(category: any): string {
    if (
      this.currentCategory &&
      this.categoryIndex === category.category.index
    ) {
      const length = this.currentCategory.questions.length;
      if (this.questionIndex === -1) {
        return '100%';
      } else {
        return (this.questionIndex / length) * 100 + '%';
      }
    } else if (this.categoryIndex > category.category.index) {
      return '100%';
    } else {
      return '0%';
    }
  }

  calculateResult(answers: any): CalculationResult {
    let co2e = 0;

    const categoryCO2e = {
      living: 0,
      travel: 0,
      food: 0,
      shopping: 0,
    };

    /* Living */

    let persons: any;
    let sizeOfHome: any;
    if (
      answers.how_many_persons_in_the_household &&
      answers.what_is_size_of_your_home
    ) {
      persons = answers.how_many_persons_in_the_household;
      sizeOfHome = answers.what_is_size_of_your_home;
      const co2eHome = (sizeOfHome.value * 8.52) / persons.value;

      co2e += co2eHome;
      categoryCO2e.living += co2eHome;
    }

    if (
      answers.how_many_persons_in_the_household &&
      answers.what_is_size_of_your_home &&
      answers.home_energy_sources
    ) {
      const energySources = answers.home_energy_sources;
      co2e +=
        (this.getLocalizedValue(energySources.values) * sizeOfHome.value) /
        persons.value;
      categoryCO2e.living +=
        (this.getLocalizedValue(energySources.values) * sizeOfHome.value) /
        persons.value;
    }

    /* Travel */

    if (
      answers.car_fuel_type &&
      answers.how_far_do_you_travel_by_car_each_week
    ) {
      const howFarYouTravelByCar =
        answers.how_far_do_you_travel_by_car_each_week;
      const emissionsFromTransport =
        this.getLocalizedValue(howFarYouTravelByCar.values) * 52;
      const fuelType = answers.car_fuel_type;
      co2e += emissionsFromTransport * fuelType.intensity;
      categoryCO2e.travel = emissionsFromTransport * fuelType.intensity;
    }

    if (answers.how_far_do_you_travel_by_public_transportation_each_week) {
      const howFarYouTravelByPublicTransport =
        answers.how_far_do_you_travel_by_public_transportation_each_week;
      co2e +=
        this.getLocalizedValue(howFarYouTravelByPublicTransport.values) *
        howFarYouTravelByPublicTransport.intensity *
        52;
      categoryCO2e.travel +=
        this.getLocalizedValue(howFarYouTravelByPublicTransport.values) *
        howFarYouTravelByPublicTransport.intensity *
        52;
    }

    if (answers.how_many_hours_you_fly_each_year) {
      const howManyHoursYouFly = answers.how_many_hours_you_fly_each_year;
      co2e +=
        this.getLocalizedValue(howManyHoursYouFly.values) *
        howManyHoursYouFly.intensity;
      categoryCO2e.travel +=
        this.getLocalizedValue(howManyHoursYouFly.values) *
        howManyHoursYouFly.intensity;
    }

    /* Food */

    if (answers.how_would_you_describe_your_diet) {
      const diet = answers.how_would_you_describe_your_diet;
      co2e += this.getLocalizedValue(diet.values);
      categoryCO2e.food += this.getLocalizedValue(diet.values);
    }

    /* Shopping */

    if (answers.goods_and_services) {
      const goods = answers.goods_and_services;
      co2e += this.getLocalizedValue(goods.values);
      categoryCO2e.shopping += this.getLocalizedValue(goods.values);
    }

    const testResult = {
      co2e,
      categoryCO2e: [
        {
          co2e: categoryCO2e.living,
          category: {
            icon: 'icon-test-living.svg',
            index: 0,
            title: 'Living',
          },
        },
        {
          co2e: categoryCO2e.travel,
          category: {
            icon: 'icon-test-travel.svg',
            index: 1,
            title: 'Travel',
          },
        },
        {
          co2e: categoryCO2e.food,
          category: {
            icon: 'icon-test-food.svg',
            index: 2,
            title: 'Food',
          },
        },
        {
          co2e: categoryCO2e.shopping,
          category: {
            icon: 'icon-test-shopping.svg',
            index: 3,
            title: 'Shopping',
          },
        },
      ],
    };

    return testResult;
  }
}
